import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Volume2, VolumeX, RotateCcw, Zap, CheckCircle2 } from "lucide-react";
import { MaskLines, Rise, SlideShell, DashMark } from "@/deck/ui";

const chips = ["AI-native sub-metering", "Private & commercial areas", "Built in Indonesia"];

export default function Cover() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 18 });
  const bgx = useSpring(useTransform(mx, [-0.5, 0.5], [24, -24]), { stiffness: 60, damping: 20 });
  const bgy = useSpring(useTransform(my, [-0.5, 0.5], [16, -16]), { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play();
  };

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play();
  };

  return (
    <div className="relative min-h-full lg:h-full" onMouseMove={onMove}>
      <motion.div className="pointer-events-none absolute -inset-10" style={{ x: bgx, y: bgy }}>
        <img src="/assets/cover.jpg" alt="" className="h-full w-full object-cover opacity-[0.22]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,transparent_0%,#07080b_70%)]" />
      </motion.div>
      <SlideShell testId="cover-slide" className="justify-center">
        <div className="grid flex-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <Rise delay={0.05} className="mb-6 flex items-center gap-3">
              <DashMark className="h-10 w-auto" />
              <span className="font-display text-xl font-semibold tracking-tight text-zinc-100" data-testid="cover-product-name">
                Dash AI Assistant
              </span>
            </Rise>
            <MaskLines
              testId="cover-headline"
              lines={["Power that", "talks back."]}
              accentIndex={1}
              className="text-[3.4rem] font-extrabold leading-[0.9] tracking-[-0.04em] text-zinc-50 sm:text-7xl lg:text-[6.6rem]"
            />
            <Rise delay={0.55} className="mt-7 max-w-lg">
              <p className="text-base leading-relaxed text-zinc-300 lg:text-lg" data-testid="cover-tagline">
                The AI assistant that meters, bills, recharges and fixes — for every tenant and Building Management in
                Indonesia's private & commercial areas.
              </p>
            </Rise>
            <Rise delay={0.7} className="mt-7 flex flex-wrap gap-2">
              {chips.map((c) => (
                <span key={c} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 font-mono-d text-[11px] uppercase tracking-[0.16em] text-zinc-300">
                  {c}
                </span>
              ))}
            </Rise>
          </div>

          <div className="lg:col-span-6" style={{ perspective: 1400 }}>
            <motion.div
              initial={{ opacity: 0, y: 40, rotateX: 18 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
            >
              <motion.div className="relative" style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
                <div className="absolute -inset-6 rounded-[40px] bg-[var(--dash)] opacity-20 blur-3xl" />
                <div className="relative overflow-hidden rounded-[28px] border border-white/15 bg-black shadow-[0_40px_120px_-30px_rgba(255,107,0,0.55)]">
                  <video
                    ref={videoRef}
                    src="/assets/intro.mp4"
                    poster="/assets/cover.jpg"
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="aspect-video w-full object-cover"
                    data-testid="cover-intro-video"
                  />
                  <div className="absolute bottom-3 right-3 flex gap-2">
                    <button onClick={replay} aria-label="Replay intro" data-testid="cover-video-replay" className="grid h-9 w-9 place-items-center rounded-full bg-black/60 text-zinc-100 backdrop-blur-md transition-colors hover:bg-[var(--dash)] hover:text-black">
                      <RotateCcw size={15} />
                    </button>
                    <button onClick={toggleSound} aria-label="Toggle sound" data-testid="cover-video-sound-toggle" className="grid h-9 w-9 place-items-center rounded-full bg-black/60 text-zinc-100 backdrop-blur-md transition-colors hover:bg-[var(--dash)] hover:text-black">
                      {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
                    </button>
                  </div>
                </div>
                <div className="absolute -left-6 -top-5 hidden items-center gap-2 rounded-2xl border border-white/10 bg-[#0e1117]/90 px-4 py-3 backdrop-blur-xl sm:flex" style={{ transform: "translateZ(60px)" }}>
                  <CheckCircle2 size={18} className="text-[var(--dash)]" />
                  <div>
                    <p className="text-xs font-semibold text-zinc-100">Recharged 50 kWh</p>
                    <p className="font-mono-d text-[11px] text-zinc-400">via QRIS · 2s</p>
                  </div>
                </div>
                <div className="absolute -bottom-6 -left-4 hidden items-center gap-3 rounded-2xl border border-dash/40 bg-black/80 px-4 py-3 backdrop-blur-xl sm:flex" style={{ transform: "translateZ(90px)" }}>
                  <Zap size={18} className="text-[var(--dash)]" />
                  <p className="text-xs text-zinc-200">"Hey Dash, how much power do I have left?"</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </SlideShell>
    </div>
  );
}
